import React, { useMemo } from "react";
import { Footer, Navbar } from "../components";
import { useSelector, useDispatch } from "react-redux";
import { addCart, delCart } from "../redux/action";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { formatRupiah } from "../utils/currency";
import { categoryLabel } from "../utils/categories";
import { mapProduct } from "../utils/productMapping";

const Cart = () => {
  const rawItems = useSelector((state) => state.handleCart);
  // Ensure items are mapped. Ideally, this should be done before adding to state, 
  // but mapping here ensures backward compatibility if state persisted.
  const items = (rawItems || []).map(mapProduct);
  
  const dispatch = useDispatch();

  const { subtotal, totalItems, total, adminFee } = useMemo(() => {
    const adminFeeCalc = 4000;
    const subtotalCalc = (items || []).reduce(
      (acc, it) => acc + (Number(it.final_price || it.price) || 0) * (Number(it.qty) || 0),
      0
    );
    const totalItemsCalc = (items || []).reduce(
      (acc, it) => acc + (Number(it.qty) || 0),
      0
    );
    const totalCalc = subtotalCalc + (items?.length ? adminFeeCalc : 0);

    return {
      subtotal: subtotalCalc,
      totalItems: totalItemsCalc,
      total: totalCalc,
      adminFee: adminFeeCalc,
    };
  }, [items]);

  const addItem = (product) => {
    dispatch(addCart(product));
    toast.success("Added one");
  };

  const removeItem = (product) => {
    dispatch(delCart(product));
    toast.success("Removed one");
  };

  const EmptyCart = () => (
    <section className="bg-zinc-950 text-white">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center">
          <h1 className="text-2xl font-extrabold sm:text-3xl">Cart</h1>
          <p className="mt-3 text-zinc-300">Your cart is empty.</p>

          <Link
            to="/product"
            className="mt-6 inline-flex items-center justify-center rounded-xl bg-white px-5 py-3 text-sm font-extrabold text-black transition hover:bg-zinc-100"
          >
            ← Continue shopping
          </Link>
        </div>
      </div>
    </section>
  );

  const CartItem = ({ item }) => (
    <div className="flex gap-4 border-b border-white/10 py-5 last:border-b-0">
      {/* Image */}
      <Link
        to={`/product/${item.id}`}
        className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-white/5 ring-1 ring-white/10 sm:h-24 sm:w-24"
      >
        <img
          src={item.image}
          alt={item.title}
          className="h-full w-full object-contain p-2"
          loading="lazy"
        />
      </Link>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              to={`/product/${item.id}`}
              className="block text-sm font-extrabold text-white hover:underline"
            >
              <span className="line-clamp-2">{item.title}</span>
            </Link>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {item.category && (
                <span className="rounded-full bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-zinc-300 ring-1 ring-white/10">
                  {categoryLabel(item.category)}
                </span>
              )}
              {item.product_type === 'digital' && (
                <span className="rounded-full bg-indigo-500/10 px-2.5 py-1 text-[11px] font-bold text-indigo-300 ring-1 ring-indigo-500/20">
                  Digital
                </span>
              )}
              {item.active_flash_sale && (
                 <span className="rounded-full bg-rose-500/15 px-2.5 py-1 text-[11px] font-extrabold text-rose-300 ring-1 ring-rose-500/20">
                   Flash Sale
                 </span>
              )}
            </div>
          </div>

          {/* Line total */}
          <div className="text-right">
            <div className="text-sm font-extrabold text-white">
              {formatRupiah(Number(item.final_price || item.price) * Number(item.qty || 0))}
            </div>
            <div className="mt-1 flex flex-col items-end gap-0.5 text-xs text-zinc-400">
               {item.active_flash_sale && (
                  <span className="line-through decoration-rose-500/50 block">
                     {item.qty} × {formatRupiah(item.price)}
                  </span>
               )}
               <span>
                  {item.qty} × {formatRupiah(item.final_price || item.price)}
               </span>
            </div>
          </div>
        </div>

        {/* Qty controls */}
        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="inline-flex items-center rounded-xl bg-white/5 ring-1 ring-white/10">
            <button
              onClick={() => removeItem(item)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-l-xl text-zinc-200 hover:bg-white/10 hover:text-white"
              aria-label="Decrease quantity"
            >
              <span className="text-xl leading-none">−</span>
            </button>

            <div className="min-w-12 px-3 text-center text-sm font-extrabold text-white">
              {item.qty}
            </div>

            <button
              onClick={() => addItem(item)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-r-xl text-zinc-200 hover:bg-white/10 hover:text-white"
              aria-label="Increase quantity"
            >
              <span className="text-xl leading-none">+</span>
            </button>
          </div>

          <button
            onClick={() => {
              // remove repeatedly by qty? tergantung reducer kamu.
              // Kalau delCart kamu ngurangin 1, tombol ini optional.
              removeItem(item);
            }}
            className="text-sm font-semibold text-zinc-300 hover:text-white"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );

  const ShowCart = () => (
    <section className="bg-zinc-950 text-white">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Title */}
        <div className="flex items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-extrabold sm:text-3xl">Cart</h1>
            <p className="mt-1 text-sm text-zinc-400">
              {totalItems} item{totalItems !== 1 ? "s" : ""} in your cart
            </p>
          </div>

          <Link
            to="/product"
            className="text-sm font-semibold text-zinc-300 hover:text-white"
          >
            Continue shopping →
          </Link>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* Items */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 sm:p-6">
              <div className="text-sm font-extrabold text-white">Item List</div>
              <div className="mt-4">
                {items.map((item) => (
                  <CartItem key={item.id} item={item} />
                ))}
              </div>
            </div>
          </div>

          {/* Summary */}
          <aside className="lg:col-span-1">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 sm:p-6">
              <div className="text-sm font-extrabold text-white">Order Summary</div>

              <div className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between text-zinc-300">
                  <span>Products ({totalItems})</span>
                  <span className="font-semibold text-white">
                    {formatRupiah(subtotal)}
                  </span>
                </div>

                <div className="flex items-center justify-between text-zinc-300">
                  <span>Biaya Admin</span>
                  <span className="font-semibold text-white">
                    {formatRupiah(adminFee)}
                  </span>
                </div>

                <div className="h-px w-full bg-white/10" />

                <div className="flex items-center justify-between">
                  <span className="text-sm font-extrabold text-white">Total</span>
                  <span className="text-lg font-extrabold text-white">
                    {formatRupiah(total)}
                  </span>
                </div>
              </div>

              <Link
                to="/checkout"
                className="mt-5 inline-flex w-full items-center justify-center rounded-xl bg-emerald-500 px-4 py-3 text-sm font-extrabold text-white transition hover:bg-emerald-400"
              >
                Go to checkout
              </Link>

              <p className="mt-3 text-xs text-zinc-400">
                Pembayaran aman • Instant download
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );

  return (
    <>
      <Navbar />
      {items?.length ? <ShowCart /> : <EmptyCart />}
      <Footer />
    </>
  );
};

export default Cart;
